import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import { LOADING, SUCCESS } from "../../constants/common";
import LoadingButton from "@mui/lab/LoadingButton";
import { useSettings } from "../../contexts/Settings";
import { Controller, useForm } from "react-hook-form";
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { passwordChangeValidationSchema } from "../../validations/validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "../../contexts/Auth";
import { trackClickEvent } from "../../mixpanel";

const PasswordSettings = () => {
  const { editPassword, updateEditStatus, handleSettingsState } = useSettings();

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmedPassword, setShowConfirmedPassword] = useState(false);
  const { permissions } = useAuth();

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
  const handleClickShowConfirmedPassword = () =>
    setShowConfirmedPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(passwordChangeValidationSchema),
    mode: "onChange",
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  });

  const onSubmit = (data) => {
    if (isValid) {
      editPassword({
        old_password: data.current_password,
        password: data.new_password,
        password_confirmation: data.confirm_password,
      });
      trackClickEvent("Updated Password Settings", {
        component: "Password Settings",
        page: "Settings",
        button: "Save",
      });
    }
  };

  useEffect(() => {
    if (updateEditStatus === SUCCESS) {
      handleSettingsState("updateEditStatus", "");
      reset();
    }
  }, [updateEditStatus, handleSettingsState, reset]);

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        p: 3,
      }}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <Box>
        <Typography variant="h5" fontWeight={600} mb={1}>
          Change your password
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          disabled={permissions?.notifications_update}
        >
          For a strong password, try choosing a unique phrase that you don't use
          on any other accounts.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography variant="body1" mb={1.5}>
            Current Password
          </Typography>
          <Controller
            name="current_password"
            control={control}
            disabled={!permissions?.notification_update}
            render={({ field }) => (
              <TextField
                {...field}
                variant="outlined"
                type={showPassword ? "text" : "password"}
                label="Current password"
                fullWidth
                error={!!errors.current_password}
                helperText={errors.current_password?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Box>

        <Box>
          <Typography variant="body1" mb={1.5}>
            New Password
          </Typography>
          <Controller
            name="new_password"
            control={control}
            disabled={!permissions?.notification_update}
            render={({ field }) => (
              <TextField
                {...field}
                sx={{ mb: 1.5 }}
                variant="outlined"
                type={showNewPassword ? "text" : "password"}
                label="New Password"
                fullWidth
                error={!!errors.new_password}
                helperText={errors.new_password?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowNewPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Controller
            name="confirm_password"
            control={control}
            disabled={!permissions?.notification_update}
            render={({ field }) => (
              <TextField
                {...field}
                variant="outlined"
                type={showConfirmedPassword ? "text" : "password"}
                label="Confirm New Password"
                fullWidth
                error={!!errors.confirm_password}
                helperText={errors.confirm_password?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowConfirmedPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showConfirmedPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Box>

        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          <Typography variant="body1" component="li" color="text.secondary">
            A minimum of 12 characters
          </Typography>
          <Typography variant="body1" component="li" color="text.secondary">
            At least one lowercase letter
          </Typography>
          <Typography variant="body1" component="li" color="text.secondary">
            At least one number
          </Typography>
          <Typography variant="body1" component="li" color="text.secondary">
            Passwords match
          </Typography>
          <Typography variant="body1" component="li" color="text.secondary">
            At least one special character from this list: #.-?!@$%^&*
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <LoadingButton
          variant="contained"
          size="large"
          sx={{ minWidth: 110 }}
          type="submit"
          loading={updateEditStatus === LOADING}
          disabled={!isValid || !permissions?.notification_update}
        >
          Save
        </LoadingButton>
      </Box>
    </Paper>
  );
};

export default PasswordSettings;
